import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TextField, InputAdornment } from '@material-ui/core';
import { css } from '@emotion/react';
import { PrimaryButton } from '@/styles/blocks';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';

const MODE_LABELS = {
    MODIFIER: { starting: 'Starting Surcharge', interval: 'Percentage Interval', adornment: '%' },
    BY_ITEM: { starting: 'Starting $/item', interval: '$/item Interval', adornment: '$' },
    BY_MILE: { starting: 'Starting $/mile', interval: '$/mile Interval', adornment: '$' },
    BY_CABINET: { starting: 'Starting $/cabinet', interval: '$/cabinet Interval', adornment: '$' },
};

// Unknown modes keep MODIFIER's wording but a $ adornment — only MODIFIER is a percentage.
const DEFAULT_LABELS = { ...MODE_LABELS.MODIFIER, adornment: '$' };

const FuelModal = ({ open, onClose, onSubmit, mode = 'MODIFIER' }) => {
    const [startRate, setStartRate] = useState(0);
    const [endRate, setEndRate] = useState(0);
    const [surchargeInterval, setSurchargeInterval] = useState(0);
    const [rateInterval, setRateInterval] = useState(0);
    const [startingSurcharge, setStartingSurcharge] = useState(0);

    const {
        starting: startingSurchargeLabel,
        interval: surchargeIntervalLabel,
        adornment: surchargeAdornment,
    } = MODE_LABELS[mode] || DEFAULT_LABELS;

    const isValid =
        startRate >= 0 &&
        endRate >= 0 &&
        surchargeInterval >= 0 &&
        rateInterval > 0 &&
        startRate < endRate &&
        startingSurcharge >= 0;

    const handleSubmit = () => {
        if (!isValid) return;

        onSubmit({
            startRate,
            endRate,
            surchargeInterval,
            rateInterval,
            startingSurcharge,
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Calculated FSC</DialogTitle>
            <DialogContent>
                <Grid container direction="column" spacing={2}>
                    <Grid item>
                        <CurrencyTextField
                            label="Starting Rate"
                            variant="outlined"
                            fullWidth
                            value={startRate}
                            onChange={(_, value) => setStartRate(value)}
                            error={startRate < 0 || (startRate >= endRate && (startRate !== 0 || endRate !== 0))}
                            helperText={
                                startRate < 0
                                    ? 'Must be non-negative'
                                    : startRate >= endRate && endRate !== 0
                                    ? 'Must be less than End Rate'
                                    : ''
                            }
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <CurrencyTextField
                            label="Ending Rate"
                            variant="outlined"
                            fullWidth
                            value={endRate}
                            onChange={(_, value) => setEndRate(value)}
                            error={endRate < 0 || (endRate <= startRate && (startRate !== 0 || endRate !== 0))}
                            helperText={
                                endRate < 0
                                    ? 'Must be non-negative'
                                    : endRate <= startRate && startRate !== 0
                                    ? 'Must be greater than Start Rate'
                                    : ''
                            }
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            label={startingSurchargeLabel}
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={startingSurcharge}
                            onChange={(e) => setStartingSurcharge(parseFloat(e.target.value))}
                            error={startingSurcharge < 0}
                            helperText={startingSurcharge < 0 ? 'Must be non-negative' : ''}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">{surchargeAdornment}</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <TextField
                            label={surchargeIntervalLabel}
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={surchargeInterval}
                            onChange={(e) => setSurchargeInterval(parseFloat(e.target.value))}
                            error={surchargeInterval < 0}
                            helperText={surchargeInterval < 0 ? 'Must be non-negative' : ''}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">{surchargeAdornment}</InputAdornment>,
                            }}
                        />
                    </Grid>
                    <Grid item>
                        <CurrencyTextField
                            label="Rate Interval"
                            variant="outlined"
                            fullWidth
                            value={rateInterval}
                            onChange={(_, value) => setRateInterval(value)}
                            error={rateInterval <= 0 && (startRate !== 0 || endRate !== 0)}
                            helperText={
                                rateInterval <= 0 && (startRate !== 0 || endRate !== 0) ? 'Must be greater than 0' : ''
                            }
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                css={css`
                    padding: 16px 24px;
                `}
            >
                <PrimaryButton onClick={onClose}>Cancel</PrimaryButton>
                <PrimaryButton onClick={handleSubmit} disabled={!isValid || (startRate === 0 && endRate === 0)}>
                    Add
                </PrimaryButton>
            </DialogActions>
        </Dialog>
    );
};

export default FuelModal;
